'use client';

import { useEffect, useState } from 'react';

import { getBookingScrollContainer } from './booking-scroll-container';

/**
 * Becomes true once the element comes within `margin` of the visible area of
 * the booking page's scroll container (the app's route page, or the window),
 * and stays true. Lets per-card requests wait until a card is about to be
 * seen instead of firing for every card a long list renders.
 */
export function useNearViewport<T extends HTMLElement>(
  enabled: boolean,
  margin = '600px',
) {
  const [element, setElement] = useState<T | null>(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    if (!enabled || isNear || !element) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsNear(true);
      },
      {
        root: getBookingScrollContainer(element),
        rootMargin: `${margin} 0px`,
      },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, isNear, element, margin]);

  return [setElement, isNear] as const;
}
